export type WizardStep =
  | 'select-type'
  | 'select-customer'
  | 'select-items'
  | 'select-invoices'
  | 'payments'
  | 'review'
  | 'success';

export type DocCategory = 'invoice' | 'receipt' | 'delivery_note' | 'invoice_receipt';
export type InvoiceSourceMode = 'items' | 'delivery_notes';

export interface DocCategoryConfig {
  id: DocCategory;
  icon: string;
  titleKey: string;
  descKey: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

export interface RivhitCustomer {
  customer_id: number;
  last_name: string;
  first_name: string;
  customer_name?: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
}

export interface RivhitItem {
  item_id?: number;
  item_name?: string;
  catalog_number?: string;
  item_group_id?: number;
  item_group_name?: string;
  price?: number;
  cost?: number;
  quantity_type?: string;
  barcode?: string;
  is_active?: boolean;
}

export interface SelectedCustomer {
  customer_id: number | null;
  display_name: string;
  is_free_text: boolean;
}

export interface DocType {
  document_type: number;
  document_name: string;
  is_invoice_receipt: boolean;
  is_accounting: boolean;
  price_include_vat: boolean;
}

export interface ReceiptType {
  receipt_type: number;
  receipt_name: string;
  is_invoice_receipt: boolean;
}

export interface PaymentTypeItem {
  payment_type: number;
  payment_name: string;
  type_code: number;
}

export interface Product {
  id: string;
  name: string;
  name_he?: string;
  category?: string;
  unit: string;
  price?: number;
  makat?: string;
}

export interface SelectedItem {
  id: string;
  item_id?: number;
  catalog_number?: string;
  description: string;
  quantity: number;
  price: number;
  unit: string;
  is_custom: boolean;
}

export interface PaymentLine {
  payment_type: number;
  payment_name: string;
  amount: number;
  check_number?: number;
  bank_code?: number;
  branch_number?: number;
  bank_account_number?: number;
  due_date?: string;
}

export interface CreatedDocument {
  document_type: number;
  document_number: number;
  document_link?: string;
  print_status?: number;
  customer_id?: number;
  delivery_note_closures?: InvoiceCloseResult[];
  failed_delivery_note_closures?: InvoiceCloseResult[];
}

export interface AssistantPrefillCustomer {
  customer_id: number | null;
  display_name: string;
  is_free_text?: boolean;
}

export interface AssistantPrefillItem {
  product_id?: string;
  item_id?: number;
  catalog_number?: string;
  description: string;
  quantity: number;
  price?: number | null;
  unit?: string;
  is_custom?: boolean;
}

export interface AssistantDetectedCapability {
  id: string;
  label: string;
  value: string;
  confidence?: number | null;
}

export interface AssistantDocumentPrefill {
  source?: 'agent_chat';
  category?: DocCategory | null;
  customer?: AssistantPrefillCustomer | null;
  items?: AssistantPrefillItem[];
  comments?: string;
  selected_document_option_id?: string | null;
  detected_capabilities?: AssistantDetectedCapability[];
}

export interface InvoiceAllocation {
  document_type: number;
  document_number: number;
  amount_close: number;
  invoice_total?: number;
  open_balance?: number;
  document_date?: string;
  due_date?: string;
}

export interface DeliveryNoteSelection {
  document_type: number;
  document_number: number;
  document_date?: string;
  document_type_name?: string;
  amount: number;
}

export interface InvoiceCloseResult {
  document_type: number;
  document_number: number;
  open_balance_before?: number;
  amount_close: number;
  remaining_balance?: number;
  close_status: 'fully_closed' | 'partially_closed' | 'closed_by_amount';
  rivhit_status: 'success' | 'failed';
  error?: string;
}

export interface ReceiptWithAllocationsResult {
  success: boolean;
  status: 'completed' | 'partial_success';
  receipt: CreatedDocument;
  allocated_amount: number;
  unallocated_amount: number;
  comment?: string;
  invoice_allocations: InvoiceCloseResult[];
  failed_invoice_allocations: InvoiceCloseResult[];
}

export interface OpenInvoice {
  document_type: number;
  document_number: number;
  document_date: string;
  document_time?: string;
  document_type_name: string;
  amount: number;
  paid_amount: number;
  open_balance: number;
  customer_id?: number;
  customer_name: string;
}

export interface OpenDeliveryNote {
  document_type: number;
  document_number: number;
  document_date: string;
  document_time?: string;
  document_type_name: string;
  amount: number;
  customer_id?: number;
  customer_name: string;
  is_closed: boolean;
}

export interface WizardData {
  category: DocCategory | null;
  docType: number | null;
  receiptType: number | null;
  invoiceSourceMode: InvoiceSourceMode;
  priceIncludeVat: boolean;
  customer: SelectedCustomer | null;
  items: SelectedItem[];
  deliveryNotes: DeliveryNoteSelection[];
  payments: PaymentLine[];
  comments: string;
  createdDoc: CreatedDocument | null;
  invoiceAllocations: InvoiceAllocation[];
  allocationsResult: ReceiptWithAllocationsResult | null;
}

export const initialWizardData: WizardData = {
  category: null,
  docType: null,
  receiptType: null,
  invoiceSourceMode: 'items',
  priceIncludeVat: false,
  customer: null,
  items: [],
  deliveryNotes: [],
  payments: [],
  comments: '',
  createdDoc: null,
  invoiceAllocations: [],
  allocationsResult: null,
};

export const DOC_CATEGORIES: DocCategoryConfig[] = [
  {
    id: 'invoice',
    icon: '📄',
    titleKey: 'createDocument.types.invoice',
    descKey: 'createDocument.types.invoiceDesc',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  {
    id: 'receipt',
    icon: '🧾',
    titleKey: 'createDocument.types.receipt',
    descKey: 'createDocument.types.receiptDesc',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
  },
  {
    id: 'delivery_note',
    icon: '🚚',
    titleKey: 'createDocument.types.deliveryNote',
    descKey: 'createDocument.types.deliveryNoteDesc',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
  },
  {
    id: 'invoice_receipt',
    icon: '📑',
    titleKey: 'createDocument.types.invoiceReceipt',
    descKey: 'createDocument.types.invoiceReceiptDesc',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
  },
];
